import React, { createContext, useState, useContext, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { createMuiTheme, ThemeProvider as MUIThemeProvider } from "@material-ui/core/styles";
import { CssBaseline } from "@material-ui/core";

const DARK_MODE_KEY = "waia_dark_mode";

// feat_328 — densidad visual. Vive acá (y no en UiPrefsProvider) porque
// UiPrefsProvider está DENTRO del ThemeProvider y no puede alimentar al theme.
const DENSITY_KEY = "waia_density";
const DENSITY_VALUES = ["comfortable", "normal", "compact"];
const DENSITY_CONFIG = {
  comfortable: { fontSize: 15, spacing: 9, bigAvatar: 56, drawerWidth: 260 },
  normal: { fontSize: 14, spacing: 8, bigAvatar: 50, drawerWidth: 240 },
  compact: { fontSize: 12, spacing: 6, bigAvatar: 40, drawerWidth: 210 },
};

// Detección automática por viewport — SOLO la primera vez (después manda
// localStorage, así una elección explícita del usuario no se pisa al
// redimensionar ni al abrir en otra pantalla).
const detectDensity = () => {
  const w = typeof window !== "undefined" ? window.innerWidth : 1280;
  if (w >= 1400) return "comfortable";
  if (w >= 1024) return "normal";
  return "compact";
};

const ThemeContext = createContext();

const scrollbarStyles = {
  "&::-webkit-scrollbar": {
    width: "8px",
    height: "8px",
  },
  "&::-webkit-scrollbar-thumb": {
    boxShadow: "inset 0 0 6px rgba(0, 0, 0, 0.3)",
    backgroundColor: "#e8e8e8",
  },
};

export const ThemeProvider = ({ children }) => {
  const [darkMode, setDarkMode] = useState(
    () => localStorage.getItem(DARK_MODE_KEY) === "true"
  );

  const [density, setDensity] = useState(() => {
    const saved = localStorage.getItem(DENSITY_KEY);
    if (saved && DENSITY_VALUES.includes(saved)) return saved;
    return detectDensity();
  });

  const toggleTheme = () => {
    setDarkMode((prevMode) => {
      const next = !prevMode;
      localStorage.setItem(DARK_MODE_KEY, String(next));
      return next;
    });
  };

  const setDensityValue = useCallback((value) => {
    if (!DENSITY_VALUES.includes(value)) return;
    localStorage.setItem(DENSITY_KEY, value);
    setDensity(value);
  }, []);

  const cfg = useMemo(
    () => DENSITY_CONFIG[density] || DENSITY_CONFIG.normal,
    [density]
  );

  const theme = useMemo(
    () =>
      createMuiTheme({
        scrollbarStyles,
        // feat_328: hasta acá el theme no definía ni typography ni spacing
        // (ambos quedaban en el default de MUI: 14 / 8).
        typography: {
          fontSize: cfg.fontSize,
          htmlFontSize: 16,
        },
        spacing: cfg.spacing,
        palette: {
          type: darkMode ? "dark" : "light",
          primary: { main: "#2576d2" },
          ...(darkMode
            ? {
                background: {
                  default: "#121212",
                  paper: "#1e1e1e",
                },
                text: {
                  primary: "#e0e0e0",
                  secondary: "#aaaaaa",
                },
              }
            : {}),
        },
        overrides: {
          MuiInputBase: {
            input: {
              "&:-webkit-autofill": {
                WebkitBoxShadow: darkMode
                  ? "0 0 0 100px #1e1e1e inset"
                  : "0 0 0 100px #fff inset",
                WebkitTextFillColor: darkMode ? "#e0e0e0" : "#000",
              },
            },
          },
          MuiOutlinedInput: {
            root: {
              "& fieldset": {
                borderColor: darkMode
                  ? "rgba(255, 255, 255, 0.23)"
                  : "rgba(0, 0, 0, 0.23)",
              },
              "&:hover fieldset": {
                borderColor: darkMode
                  ? "rgba(255, 255, 255, 0.5)"
                  : "rgba(0, 0, 0, 0.87)",
              },
            },
            input: {
              color: darkMode ? "#e0e0e0" : "inherit",
            },
          },
          MuiInputLabel: {
            root: {
              color: darkMode ? "#aaaaaa" : "inherit",
            },
          },
        },
      }),
    [darkMode, cfg]
  );

  const contextValue = useMemo(
    () => ({ darkMode, toggleTheme, density, setDensityValue, cfg }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [darkMode, density, cfg, setDensityValue]
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      <MUIThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MUIThemeProvider>
    </ThemeContext.Provider>
  );
};
ThemeProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useThemeContext = () => useContext(ThemeContext);
export { DENSITY_VALUES, DENSITY_CONFIG };
